<template>
  <MapLocationPickerLayoutV2
    :onSearchInput="onSearchInput"
    :searchingNomination="searchingNomination"
    :resolvingAddress="resolvingAddress"
    :searchOnce="searchOnce"
    :suggestions="suggestions"
    :selectSuggestion="selectSuggestion"
    :useCurrentLocation="useCurrentLocation"
    :confirmSelection="confirmSelection"
    :selectedLocation="selectedLocationForDisplay"
    :selectedCity="city"
    :additionalFields="props.additionalFields"
    :defaultValues="props.defaultValues"
    :autoUseCurrentLocation="autoUseCurrentLocation"
    @fieldValuesChanged="onFieldValuesChanged"
    @backClicked="emit('backClicked')"
  >
    <LocationSearch
      class="hidden"
      ref="locationSearch"
      @locationSelected="locationSelected"
      @suggestionAvailable="
        val => {
          suggestions = val.map(el => {
            return {
              ...el,
              display_name: el.mainText,
            }
          })
        }
      "
      @searchStart="
        () => {
          searchingNomination = true
        }
      "
      @searchEnd="
        () => {
          searchingNomination = false
        }
      "
    ></LocationSearch>
    <div class="w-full h-full overflow-hidden">
      <Map
        ref="mapRef"
        :center="mapCenter"
        :zoom="15"
        :disableDefaultUI="true"
        @ready="onMapReady"
        @mapClicked="onMapClick"
        class="w-full h-full"
      />
    </div>
  </MapLocationPickerLayoutV2>
</template>

<script setup>
import { ref, computed } from 'vue'
import { bus } from '@core/events/eventSystem'
import constants from '@core/utils/constants'
import MapLocationPickerLayoutV2 from './MapLocationPickerLayoutV2.vue'
import Map from '@features/map/Map.vue'
import {
  getCurrentPositionWithFallback,
  DEFAULT_LOCATION,
} from '@features/map/utils/locationUtils.js'
import { GoogleMapUtils } from '@features/map/uitls/googleMap/googleMapUtils.js'
import LocationSearch from '@features/map/components/LocationSearch.vue'

const props = defineProps({
  defaultPosition: {
    type: Object,
    default: () => {
      return {}
    },
  },
  additionalFields: {
    type: Array,
    default: () => {
      return []
    },
  },
  defaultValues: {
    type: Object,
    default: () => {
      return {}
    },
  },
})

const emit = defineEmits(['location-selected', 'backClicked'])

const mapRef = ref(null)
const locationSearch = ref(null)
const mapCenter = ref({ ...DEFAULT_LOCATION })
const searchingNomination = ref(false)
const mapReady = ref(false)
const resolvingAddress = ref(false)
const autoUseCurrentLocation = ref(false)

const additionalFieldValues = ref({ ...props.defaultValues })

const query = ref('')
const suggestions = ref([])
const city = ref('')

const selected = ref({
  lat: null,
  lng: null,
  address: '',
  ...(props.defaultPosition || {}),
})

const selectedLocationForDisplay = computed(() => {
  if (resolvingAddress.value) {
    return { lat: null, lng: null, address: '' }
  }
  return selected.value
})

function onMapReady() {
  mapReady.value = true
  setTimeout(() => {
    initPositionOnMapReady()
  }, 500)
}

function onMapClick(event) {
  const position = { lat: event.lat, lng: event.lng }
  updateSelectedPosition(position)
  updateMapMarker()

  city.value = ''
  reverseGeocode(position.lat, position.lng)
}

function updateSelectedPosition(position) {
  selected.value.lat = position.lat
  selected.value.lng = position.lng
  mapCenter.value = position
}

function updateMapMarker() {
  if (!mapReady.value || !selected.value.lat) return

  mapRef.value?.setMarker?.(
    {
      position: {
        lat: selected.value.lat,
        lng: selected.value.lng,
      },
      title: 'Position sélectionnée',
      iconType: 'destination',
      draggable: true,
      onDragEnd: onMarkerDragEnd,
    },
    'location-picker',
    'destination-marker',
  )
}

function onMarkerDragEnd(event) {
  const position = {
    lat: event.latLng.lat(),
    lng: event.latLng.lng(),
  }

  updateSelectedPosition(position)
  mapRef.value?.map?.panTo(position)
  reverseGeocode(position.lat, position.lng)
}

async function initPositionOnMapReady() {
  const hasDefaultPosition = selected.value.lat && selected.value.lng

  if (!hasDefaultPosition) {
    // Pas de position préalable : sélectionner automatiquement la position actuelle
    // Signal au layout pour déclencher aussi le pré-remplissage des champs utilisateur
    autoUseCurrentLocation.value = true
    await useCurrentLocation()
    // Réinitialiser après usage
    setTimeout(() => { autoUseCurrentLocation.value = false }, 200)
    return
  }

  // Sinon, centrer sur la position déjà fournie et afficher le marqueur
  resolvingAddress.value = true
  try {
    const position = { lat: selected.value.lat, lng: selected.value.lng }
    updateSelectedPosition(position)
    mapRef.value.centerOnPosition(position)
    updateMapMarker()

    if (!selected.value.address) {
      await reverseGeocode(position.lat, position.lng)
    }
  } catch {
    mapCenter.value = { ...DEFAULT_LOCATION }
  } finally {
    resolvingAddress.value = false
  }
}

let searchTimeout = null
function onSearchInput(val) {
  suggestions.value = []
  if (searchTimeout) clearTimeout(searchTimeout)
  query.value = val
  if (!query.value || query.value.length < 3) return
  searchTimeout = setTimeout(() => {
    searchingNomination.value = true
    locationSearch.value.search(query.value)
    searchingNomination.value = false
  }, 350)
}

function selectSuggestion(item) {
  locationSearch.value.selectSuggestion(item)
}

function locationSelected(item) {
  suggestions.value = []

  const lat = parseFloat(item.lat || item.coordinates?.lat)
  const lng = parseFloat(item.lon || item.coordinates?.lng)

  updateSelectedPosition({ lat, lng })
  mapRef.value.centerOnPosition({ lat, lng })
  updateMapMarker()

  selected.value.address = item.display_name || item.locationText
  city.value = extractCityFromAddress(item.address)
}

function searchOnce() {
  if (query.value) locationSearch.value.search(query.value)
}

async function reverseGeocode(lat, lon) {
  resolvingAddress.value = true
  try {
    const geocodeDetails = await GoogleMapUtils.getGeocodeDetails({ lat, lng: lon })
    const address = geocodeDetails.formatted_address
    selected.value.address = address
    query.value = address
    const extractedCity = extractCityFromGoogleAddressComponents(geocodeDetails.address_components) || extractCityFromGoogleAddress(address)
    if (extractedCity && !city.value) {
      city.value = extractedCity
    }
  } catch {
    selected.value.address = `Position: ${lat.toFixed(6)}, ${lon.toFixed(6)}`
    query.value = selected.value.address
  } finally {
    resolvingAddress.value = false
  }
}

function extractCityFromGoogleAddressComponents(components) {
  if (!components || !Array.isArray(components)) return ''
  // Prioritize locality, then administrative_area_level_2, etc.
  const cityComponent = components.find(c => c.types.includes('locality')) || 
                        components.find(c => c.types.includes('administrative_area_level_2')) ||
                        components.find(c => c.types.includes('administrative_area_level_3')) ||
                        components.find(c => c.types.includes('sublocality'))
  return cityComponent ? cityComponent.long_name : ''
}

function extractCityFromAddress(addr) {
  return (
    addr?.city ||
    addr?.town ||
    addr?.village ||
    addr?.county ||
    addr?.state ||
    ''
  )
}

function extractCityFromGoogleAddress(address) {
  // Simple extraction from Google Maps formatted address (Fallback)
  const parts = address.split(',')
  // Skip the last part which is typically the country
  if (parts.length >= 2) {
    for (let i = parts.length - 2; i >= 0; i--) {
      const part = parts[i].trim()
      // Remove postal codes like "75001" or plus codes like "C8G6+3W" at the start
      const cleanPart = part.replace(/^[\w\d\+-]+\s+/, '').trim()
      if (cleanPart && !cleanPart.match(/^\d+$/) && cleanPart.length >= 2 && cleanPart.length < 30) {
        return cleanPart.replace(/Arrondissement\s*\d*/i, '').trim()
      }
    }
  } else if (parts.length === 1) {
    return parts[0].trim().replace(/^[\w\d\+-]+\s+/, '').trim()
  }
  return ''
}

async function useCurrentLocation() {
  resolvingAddress.value = true
  try {
    const onProgressiveUpdate = (refinedPos) => {
      if (refinedPos && refinedPos.lat && refinedPos.lng) {
        const distMoved = Math.hypot(
          (selected.value.lat || 0) - refinedPos.lat,
          (selected.value.lng || 0) - refinedPos.lng
        )
        if (distMoved > 0.0001) {
          updateSelectedPosition(refinedPos)
          mapRef.value?.centerOnPosition?.(refinedPos)
          updateMapMarker()
          reverseGeocode(refinedPos.lat, refinedPos.lng)
        }
      }
    }

    const position = await getCurrentPositionWithFallback({
      preferCache: true,
      onProgressiveUpdate
    })
    updateSelectedPosition(position)
    mapRef.value?.centerOnPosition?.(position)
    updateMapMarker()
    await reverseGeocode(position.lat, position.lng)
  } catch {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'warning',
      title: 'Erreur de localisation',
      body: 'Impossible de récupérer votre position.',
    })
  } finally {
    resolvingAddress.value = false
  }
}

function confirmSelection(description = '') {
  if (!selected.value.lat) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'warning',
      title: 'Position requise',
      body: 'Veuillez sélectionner une position sur la carte.',
    })
    return
  }

  if (props.additionalFields && props.additionalFields.length > 0) {
    for (const field of props.additionalFields) {
      const value = additionalFieldValues.value[field.key]
      if (
        field.required &&
        (!value || (typeof value === 'string' && value.trim() === ''))
      ) {
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'warning',
          title: 'Champ requis',
          body: `Veuillez remplir le champ "${field.label}"`,
        })
        return
      }
    }
  }

  // Aplatir les additionalFields pour qu'ils soient au même niveau que les autres champs
  const locationData = {
    lat: selected.value.lat,
    lng: selected.value.lng,
    address: selected.value.address,
    city: city.value,
    description,
  }

  // Ajouter les additionalFields directement à l'objet racine
  if (additionalFieldValues.value && Object.keys(additionalFieldValues.value).length > 0) {
    Object.assign(locationData, additionalFieldValues.value)
  }

  emit('location-selected', locationData)
}

function onFieldValuesChanged(values) {
  additionalFieldValues.value = values
}
</script>
